import React, { useState, useEffect } from 'react'
import { mappingService } from '../services/mappingService'
import { MondayFields } from './MondayFields'
import { FreshFields } from './FreshFields'
import { CustomFields } from './CustomFields'
import { ChildItems } from './ChildItems'
import { mondayService } from '../services/mondayService'
import mondaySdk from 'monday-sdk-js'
import { useLoadFreshData } from '../hooks/useLoadFreshData'
import * as model from '../constants/model'
const monday = mondaySdk()
const { Button, Loader } = require('monday-ui-react-core')

interface MappingProps {
  mapping: Mapping
  setMapping: React.Dispatch<React.SetStateAction<any>>
  setFreshError: React.Dispatch<React.SetStateAction<boolean>>
  currentBoard: number
  getMappingBoardsLength: (length: number) => void
  accessToken: string
  setMondayError: React.Dispatch<React.SetStateAction<boolean>>
}

export const Mapping: React.FC<MappingProps> = ({ mapping, setMapping, setFreshError, currentBoard, getMappingBoardsLength, accessToken, setMondayError }) => {
  const freshAccount = { freshDomain: mapping.freshDomain, freshApiKey: mapping.freshApiKey }

  const [currBoardMapping, setCurrBoardMapping] = useState<any>()
  const [isEditable, setIsEditable] = useState<boolean>(false)
  const [boardColumns, setBoardColumns] = useState<DropDownOption[] | undefined | any>()
  const [board, setBoard] = useState<DropDownOption | null>(null)
  const [unfilledRequiredFresh, setUnfilledRequiredFresh] = useState<string[]>([])
  const [unfilledRequiredMonday, setUnfilledRequiredMonday] = useState<string[]>([])
  const [unfilledRequiredCustom, setUnfilledRequiredCustom] = useState<string[]>([])
  const [unfilledRequiredChild, setUnfilledRequiredChild] = useState<string[]>([])
  const [ticketFieldsData, setTicketFieldsData] = useState<TicketFieldsData[]>()
  const [initialAgentOptions, setInitialAgentOptions] = useState<any[]>()
  useLoadFreshData(mapping.ticketType, freshAccount, setFreshError, undefined, setTicketFieldsData, setInitialAgentOptions)

  const onSetMapping = async (field: 'customFields' | 'mondayFields' | 'ticketFields' | 'serviceRequestFields' | 'childItems', value: CustomField[] | MondayFields | TicketFields): Promise<void> => {
    setCurrBoardMapping((currBoardMapping: BoardsMapping) => ({
      ...currBoardMapping,
      [field]: value,
    }))
  }

  useEffect(() => {
    if (currBoardMapping) {
      loadBoard()
      const currBoardMappingIdx = mapping.boardsMapping.findIndex((boardMapping) => boardMapping.mondayFields.board === currBoardMapping.mondayFields.board)
      mapping.boardsMapping[currBoardMappingIdx] = currBoardMapping

      setMapping(JSON.parse(JSON.stringify(mapping)))
    }
  }, [currBoardMapping])

  const loadBoard = async () => {
    const board = await mondayService.getBoardDetails(currBoardMapping.mondayFields.board, setMondayError)
    setBoard(board!)
  }

  useEffect(() => {
    getMappingBoardsLength(mapping.boardsMapping.length)
  }, [mapping.boardsMapping])

  useEffect(() => {
    ;(async (): Promise<Mapping | undefined> => await mappingService.saveMapping(mapping, accessToken))()
  }, [mapping])

  useEffect(() => {
    getCurrentBoardMapping()
  }, [])


  /**
   * 3 scenarios when loading which board to take:
   * 1. the board on the db is the same board that the user is entered - stay the same
   * 2. the board is different than the board on db - will create new board
   * 3. the board is different but reached license limit - will send a message and will take the last mapped board from the db
   */
  const getCurrentBoardMapping = async () => {
    let boardMapping = mapping.boardsMapping.find((boardMapping) => boardMapping.mondayFields.board === currentBoard.toString()) //Every board has its own mapping
    if (boardMapping) {
      if (process.env.NODE_ENV !== 'production') console.log('should not push')
      setCurrBoardMapping(boardMapping)
    } else {
      if (process.env.NODE_ENV !== 'production') console.log('will push new one')
      const newCurrBoardsMapping = model.newBoardsMapping
      newCurrBoardsMapping.mondayFields.board = currentBoard.toString()
      setMapping((mapping: Mapping) => ({
        ...mapping,
        boardsMapping: [...mapping.boardsMapping, newCurrBoardsMapping],
      }))
      setCurrBoardMapping(newCurrBoardsMapping)
    }
  }

  const clearMapping = async (): Promise<void> => {
    const newBoardMapping = JSON.parse(JSON.stringify(model.newBoardsMapping))
    newBoardMapping.mondayFields.board = board!.value.toString()

    setCurrBoardMapping(newBoardMapping)

    monday.execute('notice', {
      message: 'Fields cleared successfully',
      type: 'success',
      timeout: 2000,
    })
  }

  const onSetBoardColumns = (boardId: string): void => {
    setBoardColumns(mondayService.getColumnsOptions(boardId, setBoardColumns, setMondayError))
  }

  const onSetIsEditable = () => {
    setIsEditable(true)

    if ((unfilledRequiredFresh.length || unfilledRequiredMonday.length || unfilledRequiredCustom.length || unfilledRequiredChild.length) && isEditable) {
      monday.execute('notice', {
        message: `Please make sure that all required fields are filled. 
        Missing: ${unfilledRequiredFresh.concat(unfilledRequiredMonday, unfilledRequiredCustom, unfilledRequiredChild).join(', ')}.`,
        type: 'error',
        timeout: 10000,
      })
      setIsEditable(true)
    } else if (isEditable) {
      monday.execute('notice', {
        message: 'Saved successfully!',
        type: 'success',
        timeout: 5000,
      })
      setIsEditable(false)
    }

    // isEditable &&
  }

  if (!currBoardMapping || !board || !ticketFieldsData)
    return (
      <div className="loading">
        <Loader hasBackground />
      </div>
    )
  return (
    <>
      <section className="mapping-container">
        <div className="mapping-fields-container">
          <MondayFields
            isEditable={isEditable}
            mappingMondayFields={currBoardMapping.mondayFields}
            onSetMapping={onSetMapping}
            onSetBoardColumns={onSetBoardColumns}
            boardColumns={boardColumns}
            currentBoard={board}
            mappingServiceFields={currBoardMapping.serviceRequestFields}
            freshAccount={freshAccount}
            setFreshError={setFreshError}
            ticketType={mapping.ticketType}
            setUnfilledRequiredMonday={setUnfilledRequiredMonday}
            setMondayError={setMondayError}
          />
          <FreshFields
            isEditable={isEditable}
            mappingTicketFields={currBoardMapping.ticketFields}
            onSetMapping={onSetMapping}
            freshAccount={freshAccount}
            boardColumns={boardColumns}
            setUnfilledRequiredFresh={setUnfilledRequiredFresh}
            ticketFieldsData={ticketFieldsData}
            setTicketFieldsData={setTicketFieldsData}
            initialAgentOptions={initialAgentOptions}
            setInitialAgentOptions={setInitialAgentOptions}
          />
          {currBoardMapping.customFields.length && mapping.ticketType === 'serviceRequest' ? (
            <CustomFields isEditable={isEditable} boardId={currBoardMapping.mondayFields.board} mappingCustomFields={currBoardMapping.customFields} boardColumns={boardColumns} onSetMapping={onSetMapping} setUnfilledRequiredCustom={setUnfilledRequiredCustom} />
          ) : null}
          {currBoardMapping.childItems.length && mapping.ticketType === 'serviceRequest' ? (
            <ChildItems isEditable={isEditable} boardId={currBoardMapping.mondayFields.board} mappingChildItems={currBoardMapping.childItems} boardColumns={boardColumns} onSetMapping={onSetMapping} setUnfilledRequiredChild={setUnfilledRequiredChild} />
          ) : null}
        </div>
        <div className="actions">
          <Button onClick={() => onSetIsEditable()}>{isEditable ? 'Save' : 'Edit'}</Button>
          <Button onClick={() => clearMapping()} disabled={!isEditable} color={Button.colors.NEGATIVE}>
            Clear
          </Button>
        </div>
      </section>
    </>
  )
}
